import styled from '@emotion/styled';
import { Constants } from '@seedlang/constants';
import React from 'react';

const Wrapper = styled.div`
  margin: 16px 0 0 0;
`;

const TableWrapper = styled.div`
  border: 1px solid #DDD;
  max-height: 320px;
  overflow: auto;
`;

const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  font-size: 13px;
  border: none !important;
`;

const TableHeaderCell = styled.th`
  background: #F3F4F6;
  border-bottom: 1px solid #DDD;
  border-right: 1px solid #DDD;
  color: #333;
  font-weight: bold;
  padding: 8px;
  position: sticky;
  text-align: left;
  top: 0;
  white-space: nowrap;
  z-index: 1;
`;

const TableCell = styled.td`
  border-bottom: 1px solid #EEE;
  border-right: 1px solid #EEE;
  background: ${props => props.warning ? '#FFF4E5' : '#FFF'};
  color: ${props => props.warning ? '#9F2A2A' : '#333'};
  padding: 8px;
  vertical-align: top;
`;

const EmptyMessage = styled.div`
  color: #666;
  font-size: 14px;
  margin: 10px 0 0 0;
`;

export const REVIEW_IMPORT_MAX_ROWS = 100;

const separators = {
  comma: ',',
  semicolon: ';',
  tab: '\t',
};

const addRow = (rows, row) => {
  if (row.some(value => `${value}`.trim().length > 0)) {
    rows.push(row);
  }
};

const parseRows = (fileText, separatorKey) => {
  const separator = separators[separatorKey] || separators.comma;
  const rows = [];
  let row = [];
  let value = '';
  let insideQuotes = false;

  for (let index = 0; index < fileText.length; index += 1) {
    const character = fileText[index];
    const nextCharacter = fileText[index + 1];

    if (character === '"') {
      if (insideQuotes && nextCharacter === '"') {
        value += '"';
        index += 1;
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (character === separator && !insideQuotes) {
      row.push(value);
      value = '';
    } else if ((character === '\n' || character === '\r') && !insideQuotes) {
      if (character === '\r' && nextCharacter === '\n') {
        index += 1;
      }
      row.push(value);
      addRow(rows, row);
      row = [];
      value = '';
    } else {
      value += character;
    }
  }

  row.push(value);
  addRow(rows, row);
  return rows;
};

export const getReviewImportPreviewRows = ({ fileText, separator, hasHeaderRow }) => {
  const parsedRows = parseRows(fileText || '', separator);
  return (hasHeaderRow ? parsedRows.slice(1) : parsedRows).map((row, index) => [
    index + 1,
    row[0] || '',
    row[1] || '',
    row[2] || '',
  ]);
};

export const reviewImportCellExceedsLimit = (row, columnIndex) => {
  if (columnIndex === 1) {
    return `${row[columnIndex] || ''}`.length > Constants.REVIEW_CARD_MAX_FRONT_TEXT_LENGTH;
  }
  if (columnIndex === 2) {
    return `${row[columnIndex] || ''}`.length > Constants.REVIEW_CARD_MAX_BACK_TEXT_LENGTH;
  }
  if (columnIndex === 3) {
    return `${row[columnIndex] || ''}`.length > Constants.REVIEW_CARD_MAX_NOTE_LENGTH;
  }
  return false;
};

export const reviewImportRowHasOverflow = row => {
  const combinedDictionaryNote = [row[2], row[3]].filter(value => `${value || ''}`.trim().length > 0).join('\n\n');
  return (
    reviewImportCellExceedsLimit(row, 1) ||
    reviewImportCellExceedsLimit(row, 2) ||
    reviewImportCellExceedsLimit(row, 3) ||
    combinedDictionaryNote.length > Constants.REVIEW_CARD_MAX_NOTE_LENGTH
  );
};

class ReviewImportPreviewTable extends React.Component {
  get rows() {
    return getReviewImportPreviewRows({
      fileText: this.props.fileText,
      separator: this.props.separator,
      hasHeaderRow: this.props.hasHeaderRow,
    });
  }

  get previewRows() {
    return this.rows;
  }

  get headers() {
    return ['', 'First Column', 'Second Column', 'Third Column'];
  }

  render() {
    if (this.previewRows.length === 0) {
      return (
        <EmptyMessage>
          No rows were found in this file.
        </EmptyMessage>
      );
    }

    return (
      <Wrapper>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                {
                  this.headers.map((header, index) => (
                    <TableHeaderCell key={`${header}-${index}`}>
                      {header}
                    </TableHeaderCell>
                  ))
                }
              </tr>
            </thead>
            <tbody>
              {
                this.previewRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {
                      this.headers.map((header, columnIndex) => (
                        <TableCell
                          key={`${rowIndex}-${header}-${columnIndex}`}
                          warning={reviewImportCellExceedsLimit(row, columnIndex)}
                        >
                          {row[columnIndex]}
                        </TableCell>
                      ))
                    }
                  </tr>
                ))
              }
            </tbody>
          </Table>
        </TableWrapper>
      </Wrapper>
    );
  }
}

export default ReviewImportPreviewTable;
